<!-- Modern confirm dialog -->
<p-confirmDialog
  key="transactionQueryConfirm"
  styleClass="!rounded-xl !shadow-2xl !border-0 !max-w-md"
  *transloco="let t"
  #txnConfirmDialog
>
  <ng-template #header>
    <div class="flex items-center gap-3 px-2">
      <div
        class="w-10 h-10 rounded-full bg-orange-light2/30 flex items-center justify-center"
      >
        <span class="icon-helpQuestion-fill text-orange-default text-xl"></span>
      </div>
      <h3 class="font-semibold text-grey-dark text-lg">
        {{ t("confirm_transaction_query") }}
      </h3>
    </div>
  </ng-template>
  <ng-template #message>
    <p class="text-sm text-grey-default px-2 py-2">
      {{ t("confirm_transaction_query_message") }}
    </p>
  </ng-template>
  <ng-template #footer>
    <div class="flex items-center justify-end gap-3 px-2 pt-2">
      <button
        (click)="txnConfirmDialog.onReject()"
        class="px-4 py-2 text-sm font-medium text-grey-default hover:text-grey-dark border border-grey-light hover:border-grey-default rounded-lg transition-colors"
      >
        {{ t("cancel") }}
      </button>
      <button
        (click)="txnConfirmDialog.onAccept()"
        class="px-5 py-2 text-sm font-semibold text-white bg-orange-default hover:bg-orange-dark1 rounded-lg transition-colors shadow-md"
      >
        {{ t("submit") }}
      </button>
    </div>
  </ng-template>
</p-confirmDialog>

<!-- Page header - clean, no step indicators -->
<div *transloco="let t" class="pwc-linear-gradient px-8 pt-6 pb-14">
  <h1 class="text-white font-serif font-extralight text-3xl tracking-wide">
    {{ t("page_transaction_query") }}
  </h1>
</div>

<div class="px-8 pb-10 -mt-8" *transloco="let t">
  <div *ngIf="!visibilityToggle">
    <!-- Selection row: Engagement + VAT Number + Bank Account side by side -->
    <div
      class="bg-white rounded-xl shadow-lg overflow-hidden mb-6 transition-all duration-300"
    >
      <div
        class="px-5 py-3.5 border-b border-grey-light bg-grey-darker flex items-center gap-2"
      >
        <span class="icon-search-fill text-white/70 text-sm"></span>
        <span class="text-white font-medium text-sm tracking-wide">{{
          t("select_engagement")
        }}</span>
      </div>

      <div
        class="p-5"
        [ngClass]="{ 'opacity-50 pointer-events-none': !submitEnabled }"
      >
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <!-- Engagement list -->
          <div class="min-w-0">
            <label
              class="block text-[11px] font-semibold text-grey-default uppercase tracking-wider mb-1.5"
              >{{ t("select_engagement") }}</label
            >
            <p-listbox
              #engagementNameListbox
              *ngIf="engagements"
              styleClass="w-full filter-text-sm rounded-lg"
              [style]="{ height: '280px' }"
              [options]="engagements"
              (onChange)="onEngagementNameListboxChange($event)"
              [filter]="true"
              [filterPlaceHolder]="t('search_name_jobcode_vatnumber')"
              [emptyMessage]="loading ? t('loading') : t('empty_message')"
              optionLabel="searchField"
            >
              <ng-template #item let-engagement>
                <div class="w-full py-0.5">
                  <p class="flex items-center justify-between gap-2">
                    <span class="font-medium text-grey-dark text-sm truncate">{{
                      engagement.name
                    }}</span>
                    <span
                      class="shrink-0 text-[11px] px-1.5 py-0.5 rounded bg-grey-lighter text-grey-default font-mono"
                      >{{ engagement.jobCode }}</span
                    >
                  </p>
                </div>
              </ng-template>
              <ng-template #footer>
                <ng-container
                  *ngTemplateOutlet="
                    listboxFooterTemplate;
                    context: {
                      $implicit: {
                        listbox: engagementNameListbox,
                        total: engagements.length,
                      },
                    }
                  "
                ></ng-container>
              </ng-template>
            </p-listbox>
          </div>

          <!-- VAT number list -->
          <div
            class="min-w-0 transition-all duration-300"
            [ngClass]="
              selectedEngagement.jobCode
                ? 'opacity-100'
                : 'opacity-30 pointer-events-none'
            "
          >
            <label
              class="block text-[11px] font-semibold text-grey-default uppercase tracking-wider mb-1.5"
              >{{ t("search_vatnumber") }}</label
            >
            <p-listbox
              #engagementVatNumbersListbox
              styleClass="w-full filter-text-sm rounded-lg"
              [style]="{ height: '280px' }"
              [(ngModel)]="selectedVatNumber"
              [options]="vatNumbers"
              (onChange)="onEngagementVatNumbersListboxChange($event)"
              [filter]="true"
              [filterPlaceHolder]="t('search_vatnumber')"
            >
              <ng-template #item let-vatNumber>
                <div class="flex items-center justify-between gap-1">
                  <span class="font-mono text-grey-dark text-xs truncate">{{
                    vatNumber
                  }}</span>
                  <span class="shrink-0 text-[11px]">
                    <span
                      *ngIf="
                        selectedVatNumber !== vatNumber ||
                        isValidVatNumber === undefined
                      "
                      class="icon-helpQuestion-outline text-grey-light"
                    ></span>
                    <span
                      *ngIf="
                        selectedVatNumber === vatNumber &&
                        isValidVatNumber === false
                      "
                      class="icon-alert-fill text-status-red"
                    ></span>
                    <span
                      *ngIf="
                        selectedVatNumber === vatNumber &&
                        isValidVatNumber === true
                      "
                      class="icon-circleCheckmark-fill text-status-green"
                    ></span>
                  </span>
                </div>
              </ng-template>
              <ng-template #footer>
                <ng-container
                  *ngTemplateOutlet="
                    listboxFooterTemplate;
                    context: {
                      $implicit: {
                        listbox: engagementVatNumbersListbox,
                        total:
                          selectedEngagement.vatNumbersWithDownloadType.length,
                      },
                    }
                  "
                ></ng-container>
              </ng-template>
            </p-listbox>
          </div>

          <!-- Bank Account list -->
          <div
            class="min-w-0 transition-all duration-300"
            [ngClass]="
              selectedVatNumber
                ? 'opacity-100'
                : 'opacity-30 pointer-events-none'
            "
          >
            <label
              class="block text-[11px] font-semibold text-grey-default uppercase tracking-wider mb-1.5"
              >{{ t("select_bank_account") }}</label
            >

            <!-- Loading -->
            <div
              *ngIf="selectedVatNumber && loadingBankAccounts"
              class="flex flex-col items-center justify-center h-[280px] gap-3"
            >
              <span
                class="animate-spin icon-refresh-fill text-orange-default text-2xl"
              ></span>
              <span class="text-sm text-grey-medium">{{ t("loading") }}</span>
            </div>

            <!-- Empty state -->
            <div
              *ngIf="
                selectedVatNumber &&
                !loadingBankAccounts &&
                bankAccounts.length === 0
              "
              class="flex flex-col items-center justify-center h-[280px] text-center"
            >
              <span
                class="icon-alert-fill text-3xl text-grey-light mb-2"
              ></span>
              <p class="text-sm text-grey-medium">
                {{ t("no_bank_accounts_available") }}
              </p>
            </div>

            <!-- Placeholder when no VAT selected -->
            <div
              *ngIf="!selectedVatNumber"
              class="flex flex-col items-center justify-center h-[280px] text-center"
            ></div>

            <!-- Bank account cards -->
            <div
              *ngIf="
                selectedVatNumber &&
                !loadingBankAccounts &&
                bankAccounts.length > 0
              "
              class="flex flex-col gap-2.5 max-h-[280px] overflow-y-auto pr-1"
            >
              <div
                *ngFor="let account of bankAccounts"
                class="relative flex items-center gap-3 border-2 rounded-lg p-3 cursor-pointer transition-all duration-200"
                [ngClass]="{
                  'border-orange-default bg-orange-light2/10 shadow-sm':
                    selectedBankAccount?.bankAccountId ===
                    account.bankAccountId,
                  'border-grey-light hover:border-orange-light1 hover:shadow-sm':
                    selectedBankAccount?.bankAccountId !==
                    account.bankAccountId,
                }"
                (click)="onBankAccountCardSelected(account)"
              >
                <div
                  class="shrink-0 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center"
                  [ngClass]="{
                    'border-orange-default bg-orange-default':
                      selectedBankAccount?.bankAccountId ===
                      account.bankAccountId,
                    'border-grey-light':
                      selectedBankAccount?.bankAccountId !==
                      account.bankAccountId,
                  }"
                >
                  <span
                    *ngIf="
                      selectedBankAccount?.bankAccountId ===
                      account.bankAccountId
                    "
                    class="w-1.5 h-1.5 rounded-full bg-white block"
                  ></span>
                </div>
                <div class="min-w-0">
                  <p
                    class="font-mono font-semibold text-grey-dark text-sm leading-tight"
                  >
                    {{ account.accountNumber }}
                  </p>
                  <p class="text-[11px] text-grey-medium mt-0.5">
                    {{ account.bankName }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Report selection -->
    <div
      *ngIf="selectedBankAccount"
      class="bg-white rounded-xl shadow-lg overflow-hidden mb-6 transition-all duration-500"
    >
      <div
        class="px-5 py-3.5 border-b border-grey-light bg-grey-darker flex items-center gap-2"
      >
        <span class="icon-analytics-fill text-white/70 text-sm"></span>
        <span class="text-white font-medium text-sm tracking-wide">{{
          t("select_reports")
        }}</span>
      </div>

      <div
        class="p-5"
        [ngClass]="{ 'opacity-50 pointer-events-none': !submitEnabled }"
      >
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <!-- Dataset card -->
          <div
            class="group relative border-2 rounded-xl overflow-hidden transition-all duration-300 cursor-pointer select-none"
            [ngClass]="{
              'border-orange-default bg-orange-light2/5 shadow-md':
                submitModel.datasetRequested,
              'border-grey-light hover:border-orange-light1 hover:shadow-sm':
                !submitModel.datasetRequested,
            }"
            (click)="toggleDataset()"
          >
            <div class="p-5">
              <div class="flex items-center gap-4">
                <!-- Icon -->
                <div
                  class="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0"
                  [ngClass]="
                    submitModel.datasetRequested
                      ? 'bg-orange-default text-white shadow-lg shadow-orange-default/30'
                      : 'bg-grey-lighter text-grey-default group-hover:bg-orange-light2/30 group-hover:text-orange-default'
                  "
                >
                  <span class="icon-downloadBox-fill text-2xl"></span>
                </div>
                <!-- Title -->
                <div class="flex-1">
                  <h4 class="font-semibold text-grey-dark text-base">
                    Dataset
                  </h4>
                  <p class="text-xs text-grey-medium mt-0.5">Raw data export</p>
                </div>
                <!-- Checkbox indicator -->
                <div
                  class="w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all duration-200 shrink-0"
                  [ngClass]="
                    submitModel.datasetRequested
                      ? 'border-orange-default bg-orange-default'
                      : 'border-grey-light group-hover:border-orange-light1'
                  "
                >
                  <span
                    *ngIf="submitModel.datasetRequested"
                    class="icon-circleCheckmark-fill text-white text-base"
                  ></span>
                </div>
              </div>

              <!-- Date range (expanded when selected) -->
              <div
                *ngIf="submitModel.datasetRequested"
                class="mt-4 pt-4 border-t border-grey-lighter"
                (click)="$event.stopPropagation()"
              >
                <!-- Suggested date range banner -->
                <div
                  *ngIf="
                    suggestedDateRangeFor === 'dataset' &&
                    suggestedDateRange.length === 2
                  "
                  class="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-orange-light2/20 border border-orange-light2"
                >
                  <span
                    class="icon-time-fill text-orange-default text-sm"
                  ></span>
                  <span class="text-xs text-grey-dark flex-1">
                    {{ t("use_same_date_range") }}:
                    <strong
                      >{{ suggestedDateRange[0] }} –
                      {{ suggestedDateRange[1] }}</strong
                    >
                  </span>
                  <button
                    (click)="acceptSuggestedDateRange('dataset')"
                    class="px-2.5 py-1 text-xs font-semibold text-white bg-orange-default hover:bg-orange-dark1 rounded transition-colors"
                  >
                    {{ t("accept") }}
                  </button>
                  <button
                    (click)="dismissSuggestedDateRange()"
                    class="px-2 py-1 text-xs text-grey-default hover:text-grey-dark transition-colors"
                  >
                    ✕
                  </button>
                </div>

                <div
                  *ngIf="availableDateRangeText"
                  class="flex items-center gap-1.5 mb-2 text-xs text-grey-medium"
                >
                  <span class="icon-time-fill text-orange-default text-sm"></span>
                  <span>{{ t("data_available") }}: <strong class="text-grey-dark">{{ availableDateRangeText }}</strong></span>
                </div>

                <span
                  class="block text-[11px] font-semibold text-grey-default uppercase tracking-wider mb-2"
                  >{{ t("date_range") }}</span
                >
                <form-row
                  template="dateRange"
                  fieldName="datasetDateRange"
                  [model]="this"
                  calendarDataType="string"
                  [dateOptions]="datePickerOptions"
                  [hideLabel]="true"
                ></form-row>
              </div>
            </div>
          </div>

          <!-- Power BI card -->
          <div
            class="group relative border-2 rounded-xl overflow-hidden transition-all duration-300 cursor-not-allowed select-none opacity-60 border-grey-light bg-grey-lighter/30"
            (click)="showPowerBiUnavailableMessage()"
            [attr.aria-disabled]="true"
          >
            <div class="p-5">
              <div class="flex items-center gap-4">
                <div
                  class="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0 bg-grey-lighter text-grey-default"
                >
                  <span class="icon-downloadBox-fill text-2xl"></span>
                </div>
                <!-- Title -->
                <div class="flex-1">
                  <h4 class="font-semibold text-grey-dark text-base">
                    Power BI
                  </h4>
                  <p class="text-xs text-grey-medium mt-0.5">
                    Interactive report
                  </p>
                </div>
                <!-- Checkbox indicator -->
                <div
                  class="w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all duration-200 shrink-0 border-grey-light"
                >
                  <span
                    *ngIf="submitModel.powerBiRequested"
                    class="icon-circleCheckmark-fill text-white text-base"
                  ></span>
                </div>
              </div>

              <!-- Date range (expanded when selected) -->
              <div
                *ngIf="submitModel.powerBiRequested"
                class="mt-4 pt-4 border-t border-grey-lighter"
                (click)="$event.stopPropagation()"
              >
                <!-- Suggested date range banner -->
                <div
                  *ngIf="
                    suggestedDateRangeFor === 'powerbi' &&
                    suggestedDateRange.length === 2
                  "
                  class="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-orange-light2/20 border border-orange-light2"
                >
                  <span
                    class="icon-time-fill text-orange-default text-sm"
                  ></span>
                  <span class="text-xs text-grey-dark flex-1">
                    {{ t("use_same_date_range") }}:
                    <strong
                      >{{ suggestedDateRange[0] }} –
                      {{ suggestedDateRange[1] }}</strong
                    >
                  </span>
                  <button
                    (click)="acceptSuggestedDateRange('powerbi')"
                    class="px-2.5 py-1 text-xs font-semibold text-white bg-orange-default hover:bg-orange-dark1 rounded transition-colors"
                  >
                    {{ t("accept") }}
                  </button>
                  <button
                    (click)="dismissSuggestedDateRange()"
                    class="px-2 py-1 text-xs text-grey-default hover:text-grey-dark transition-colors"
                  >
                    ✕
                  </button>
                </div>

                <div
                  *ngIf="availableDateRangeText"
                  class="flex items-center gap-1.5 mb-2 text-xs text-grey-medium"
                >
                  <span class="icon-time-fill text-orange-default text-sm"></span>
                  <span>{{ t("data_available") }}: <strong class="text-grey-dark">{{ availableDateRangeText }}</strong></span>
                </div>

                <span
                  class="block text-[11px] font-semibold text-grey-default uppercase tracking-wider mb-2"
                  >{{ t("date_range") }}</span
                >
                <form-row
                  template="dateRange"
                  fieldName="powerBiDateRange"
                  [model]="this"
                  calendarDataType="string"
                  [dateOptions]="datePickerOptions"
                  [hideLabel]="true"
                ></form-row>
              </div>
            </div>
          </div>

          <!-- Consent Status Dashboard link card -->
          <div
            class="group relative border-2 rounded-xl overflow-hidden transition-all duration-300 select-none border-grey-light hover:border-orange-light1 hover:shadow-sm xl:col-span-2"
          >
            <div class="p-5">
              <div class="flex items-center gap-4">
                <div
                  class="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 shrink-0 bg-grey-lighter text-grey-default group-hover:bg-orange-light2/30 group-hover:text-orange-default"
                >
                  <span class="icon-barGraph-fill text-2xl"></span>
                </div>
                <!-- Title -->
                <div class="flex-1">
                  <h4 class="font-semibold text-grey-dark text-base">
                    {{ t("consent_status_dashboard") }}
                  </h4>
                  <p class="text-xs text-grey-medium mt-0.5">
                    {{ t("consent_status_dashboard_desc") }}
                  </p>
                </div>
                <!-- Open link button -->
                <a
                  href="https://app.powerbi.com/reportEmbed?reportId=43a4e448-6d6a-4b44-9ed7-3e6e186fa87b&autoAuth=true&ctid=513294a0-3e20-41b2-a970-6d30bf1546fa"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-orange-default hover:bg-orange-dark1 rounded-lg transition-colors shadow-md"
                  (click)="$event.stopPropagation()"
                >
                  <span class="icon-externalLink-outline text-base"></span>
                  {{ t("open_dashboard") }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit section -->
    <div
      *ngIf="selectedBankAccount"
      class="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500"
    >
      <div class="p-5">
        <!-- Submit state -->
        <div
          *ngIf="submitEnabled"
          class="flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            (click)="showSubmitConfirm()"
            class="flex items-center gap-2 px-6 py-3 bg-orange-default hover:bg-orange-dark1 active:bg-orange-dark2 text-white font-semibold text-sm rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <span class="icon-play-fill text-base"></span>
            {{ t("submit_transaction_query") }}
          </button>
          <div class="text-sm text-grey-default flex items-center gap-1.5">
            <span class="icon-helpQuestion-outline text-xs"></span>
            {{ t("transaction_query_hint") }}
          </div>
        </div>

        <!-- Success state -->
        <div
          *ngIf="!submitEnabled"
          class="flex flex-col items-center py-6 text-center"
        >
          <div
            class="w-14 h-14 rounded-full bg-green-lighter flex items-center justify-center mb-3"
          >
            <span
              class="icon-circleCheckmark-fill text-3xl text-status-green"
            ></span>
          </div>
          <p class="text-base font-semibold text-grey-dark mb-1">
            {{ t("transaction_query_success") }}
          </p>
          <p class="text-sm text-grey-default mb-5">
            {{ t("transaction_query_success_detail") }}
          </p>
          <div class="flex flex-wrap items-center justify-center gap-3">
            <button
              (click)="reset()"
              class="flex items-center gap-2 px-5 py-2.5 border-2 border-grey-light hover:border-orange-default text-grey-dark hover:text-orange-default font-medium text-sm rounded-lg transition-all duration-200"
            >
              <span class="icon-refresh-fill text-sm"></span>
              {{ t("new_transaction_query") }}
            </button>
            <a
              [routerLink]="['/' + statusMonitorPageRoute]"
              class="flex items-center gap-1.5 px-5 py-2.5 text-orange-default hover:text-orange-dark1 font-medium text-sm transition-colors"
            >
              <span class="icon-time-fill text-sm"></span>
              {{ t("check_invoice_query_status_here") }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<ng-template #listboxFooterTemplate let-data>
  <div
    class="px-3 py-1.5 text-[10px] text-right select-none text-grey-default border-t border-grey-light"
  >
    {{
      data.listbox._filteredOptions
        ? data.listbox._filteredOptions.length
        : data.total
    }}/{{ data.total }}
  </div>
</ng-template>
